// The approval box of a task awaiting approval, shared by the task drawer and the approvals queue:
// the editable draft and the feedback form ("Retenir comme règle pour cet agent", "Refaire avec ce
// retour"), plus an action bar — MODIFIER, REFUSER, APPROUVER — that the caller puts in the
// drawer's footer (`box.bar`), so the decision is always on screen whatever the draft's length.
// The server enforces the permission; callers only build the box when ctx.can('operate', task
// scope) is true.
import { h, icon, setText } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { isConflict } from '../../api.js';
import { createDraftEditor, createRejectForm, guardEscape } from './components.js';
import { jsonDraftProblem, summaryFromDetail } from './model.js';
import './strings.js';

/**
 * Applies the answer of a task action to the store (so lists update before the SSE event).
 * @param {object} ctx
 * @param {unknown} res the API answer (a task, a summary, {task} or anything else)
 */
export function applyTaskResponse(ctx, res) {
  const task = res && typeof res === 'object' && res.task && typeof res.task === 'object' ? res.task : res;
  const summary = summaryFromDetail(task);
  if (summary) ctx.store.applyTask?.(summary);
  return summary;
}

/**
 * Builds the approval box of a task.
 * @param {object} ctx app context
 * @param {object} task the task detail (needs id, draft, outputFormat)
 * @param {{onDone?: (kind: 'approved'|'rejected'|'conflict', res?: object, body?: object) => void,
 *   onDirtyChange?: (dirty: boolean) => void, onBarChange?: (shown: boolean) => void, shortcuts?: boolean,
 *   compact?: boolean}} [opts]  onBarChange: the action bar hides while the feedback form is open
 * @returns {{el: HTMLElement, bar: HTMLElement, key: string, draft: string, approve(): Promise<void>, openReject(): void,
 *   closeReject(): void, toggleEdit(): void, isDirty(): boolean, isBusy(): boolean, isRejecting(): boolean,
 *   focus(): void, destroy(): void}}
 */
export function createApprovalBox(ctx, task, opts = {}) {
  const id = String(task.id);
  const path = `/api/tasks/${encodeURIComponent(id)}`;
  const outputFormat = task.outputFormat === 'json' ? 'json' : 'text';
  let busy = false;
  let rejecting = false;
  let destroyed = false;

  const editor = createDraftEditor({
    text: typeof task.draft === 'string' ? task.draft : '',
    outputFormat,
    label: t('td.section.draft'),
    onChange: () => opts.onDirtyChange?.(editor.isDirty()),
    toggleInHead: false,
    toggleKey: opts.shortcuts ? 'E' : '',
  });

  const approveLabel = h('span', { class: 'btn-label' }, t('approval.approve'));
  const approveBtn = h('button', {
    type: 'button', class: 'btn btn-primary td-approve', onClick: () => approve(),
    'aria-keyshortcuts': opts.shortcuts ? 'A' : null,
  }, icon('check', { size: 16 }), approveLabel, opts.shortcuts ? h('kbd', { class: 'kbd td-kbd', 'aria-hidden': 'true' }, 'A') : null);
  const rejectBtn = h('button', {
    type: 'button', class: 'btn btn-secondary td-reject-open', onClick: () => openReject(),
    'aria-keyshortcuts': opts.shortcuts ? 'R' : null, 'aria-expanded': 'false',
  }, icon('close', { size: 16 }), h('span', { class: 'btn-label' }, t('approval.reject')),
  opts.shortcuts ? h('kbd', { class: 'kbd td-kbd', 'aria-hidden': 'true' }, 'R') : null);
  // The action bar (sticky: the caller mounts it in the drawer's footer).
  const bar = h('div', { class: 'td-approval-bar', role: 'group', 'aria-label': t('td.approval.actions') },
    editor.toggleBtn, h('span', { class: 'spacer' }), rejectBtn, approveBtn);

  const rejectForm = createRejectForm({
    onSubmit: (body) => reject(body),
    onCancel: () => closeReject(),
    // Keeping feedback as a rule of the agent needs manage (SPEC §8); the server refuses it below.
    allowRemember: typeof ctx.can === 'function' ? ctx.can('manage', { floorId: task.floorId, deptId: task.deptId }) : true,
  });
  // Escape in the feedback closes the form instead of the whole drawer (the text would be lost).
  const feedbackArea = rejectForm.el.querySelector?.('textarea') || null;
  const offEsc = feedbackArea ? guardEscape(feedbackArea, () => rejecting && !busy, () => closeReject()) : () => {};
  const rejectWrap = h('div', { class: 'td-reject-wrap', hidden: true },
    h('p', { class: 'td-reject-title mono-label' }, t('td.reject.title')), rejectForm.el);

  const el = h('section', {
    class: ['td-approval', { 'is-compact': !!opts.compact }], 'aria-label': t('td.approval.title'),
    dataset: { format: outputFormat },
  },
  h('div', { class: 'td-approval-head' },
    h('span', { class: 'td-approval-icon', 'aria-hidden': 'true' }, icon('inbox', { size: 16 })),
    h('div', { class: 'td-approval-titles' },
      h('p', { class: 'td-approval-title mono-label' }, t('td.approval.title')),
      h('p', { class: 'td-approval-hint' }, t('td.approval.hint')))),
  editor.el, rejectWrap);

  function setBusy(on) {
    busy = !!on;
    approveBtn.disabled = busy;
    rejectBtn.disabled = busy;
    editor.toggleBtn.disabled = busy;
    approveBtn.classList.toggle('is-busy', busy);
    setText(approveLabel, busy ? t('td.approval.approving') : t('approval.approve'));
    el.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  // A conflict (the task changed state) reloads the task; any other error, a 409 staleClient (this
  // page is older than the build served) included, keeps the box and what was typed in it.
  function handleError(err) {
    if (isConflict(err)) {
      ctx.toast?.info?.(err.message || t('td.conflict'));
      opts.onDone?.('conflict');
      return;
    }
    ctx.toast?.error?.(err);
  }

  async function approve() {
    if (busy || rejecting || destroyed) return;
    const body = editor.approveBody();
    if (outputFormat === 'json' && body.editedDraft !== undefined) {
      const problem = jsonDraftProblem(body.editedDraft);
      if (problem) {
        editor.setError(problem);
        if (!editor.isEditing()) editor.setEditing(true, { focus: true });
        return;
      }
    }
    if (body.editedDraft !== undefined && !body.editedDraft.trim()) {
      editor.setError(t('td.approval.noDraft'));
      return;
    }
    setBusy(true);
    try {
      const res = await ctx.api.post(`${path}/approve`, body);
      // The box may be gone already (the live event can arrive first): still confirm the decision.
      const summary = applyTaskResponse(ctx, res);
      ctx.toast?.success?.(summary?.state === 'executing' || summary?.state === 'queued' ? t('td.approve.executing') : t('td.approve.done'));
      if (destroyed) return;
      opts.onDone?.('approved', summary || res, body);
    } catch (err) {
      if (!destroyed) handleError(err);
    } finally {
      if (!destroyed) setBusy(false);
    }
  }

  async function reject(body) {
    if (busy || destroyed) return;
    setBusy(true);
    try {
      const res = await ctx.api.post(`${path}/reject`, body);
      const summary = applyTaskResponse(ctx, res);
      ctx.toast?.success?.(body.rerun ? t('td.reject.rerunDone') : t('td.reject.finalDone'));
      if (destroyed) return;
      opts.onDone?.('rejected', summary || res, body);
    } catch (err) {
      if (destroyed) return;
      if (isConflict(err)) { handleError(err); return; }
      throw err; // shown under the feedback field by the form
    } finally {
      if (!destroyed) setBusy(false);
    }
  }

  function openReject() {
    if (busy || destroyed) return;
    rejecting = true;
    rejectWrap.hidden = false;
    bar.hidden = true;
    opts.onBarChange?.(false);
    rejectBtn.setAttribute('aria-expanded', 'true');
    el.classList.add('is-rejecting');
    rejectForm.focus();
  }

  function closeReject() {
    rejecting = false;
    rejectWrap.hidden = true;
    bar.hidden = false;
    opts.onBarChange?.(true);
    rejectBtn.setAttribute('aria-expanded', 'false');
    el.classList.remove('is-rejecting');
    rejectBtn.focus?.();
  }

  return {
    el,
    bar,
    key: `${id}|${task.waitingSince ?? ''}|${task.revisions ?? 0}`,
    draft: typeof task.draft === 'string' ? task.draft : '',
    approve,
    openReject,
    closeReject,
    toggleEdit: () => editor.toggle(),
    isDirty: () => editor.isDirty() || (rejecting && rejectForm.isDirty()),
    isBusy: () => busy,
    isRejecting: () => rejecting,
    focus: () => (rejecting ? rejectForm.focus() : approveBtn.focus?.()),
    destroy() {
      destroyed = true;
      editor.destroy();
      offEsc();
    },
  };
}
